"use client";

import { useState } from "react";
import { Phone, MessageCircle, ChevronDown, Quote, Star, CircleQuestionMark as HelpCircle, ShieldCheck, Headphones } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface Testimonial {
  name: string;
  role: string;
  company: string;
  initials: string;
  rating: number;
  quote: string;
}
interface FAQItem {
  question: string;
  answer: string;
}
const testimonials: Testimonial[] = [{
  name: "Aldo Quijano",
  role: "Director de Tecnología",
  company: "Grupo Ethnix - USA",
  initials: "AQ",
  rating: 5,
  quote: "La migración hacia la central PBX en la nube y la integración directa con Microsoft Teams redujo nuestros costos operativos en un 40% y permitió a nuestras sucursales comunicarse sin interrupciones."
}, {
  name: "Diego Mora",
  role: "Gerente de Infraestructura • Corporación Grupo M\xA0•",
  company: "",
  initials: "DM",
  rating: 5,
  quote: "\"\"El soporte técnico de AVTEC es extraordinario. La implementación de una solución 360 de llamadas y Chat nos permite tener control de nuestros canales y vender más.\""
}, {
  name: "Bernard Hernandez",
  role: "Jefe de Informatica • Yamaha Costa Rica.",
  company: "",
  initials: "BH",
  rating: 5,
  quote: "\"Integramos centrales telefónicas PBX-Vtec con nuestro SAP nos permite llevar el control de ventas en nuestro call center en un WallBoard e integrarnos de manera inteligente.\""
}];
const faqs: FAQItem[] = [{
  question: "¿Cómo funciona la integración de telefonía con Microsoft Teams?",
  answer: "Conectamos su infraestructura telefónica (PBX en la nube o local) mediante Direct Routing y SBC homologados. Esto permite que sus colaboradores realicen y reciban llamadas externas directamente desde la aplicación de Microsoft Teams en computadoras, teléfonos móviles y dispositivos de sala, sin necesidad de cambiar sus números telefónicos actuales."
}, {
  question: "¿Cuánto tiempo toma la instalación y puesta en marcha del sistema?",
  answer: "Para sistemas en la nube y telefonía virtual, el despliegue inicial suele completarse entre 24 y 72 horas hábiles. En proyectos integrales con aprovisionamiento de hardware físico (SBC, gateways o teléfonos IP corporativos), la planificación y entrega toma habitualmente entre 5 y 10 días laborales según la dimensión de su red."
}, {
  question: "¿Qué nivel de soporte técnico y monitoreo ofrecen?",
  answer: "Ofrecemos contratos de soporte técnico especializado 24/7 y 8x5 con tiempos de respuesta garantizados por SLA. Nuestro equipo de ingenieros certificados atiende incidencias de forma remota y presencial, además de mantener monitoreo proactivo de calidad de llamadas y disponibilidad de enlaces."
}, {
  question: "¿Son compatibles mis teléfonos IP y gateways actuales?",
  answer: "Sí. Nuestras soluciones basadas en estándares abiertos SIP (Grandstream, 3CX, Dinstar, Yealink) son compatibles con la inmensa mayoría de terminales SIP del mercado. Realizamos un diagnóstico inicial para evaluar y reutilizar su equipamiento existente siempre que cumpla con los estándares de seguridad."
}, {
  question: "¿Cómo garantizan la seguridad de voz frente a fraude y ataques?",
  answer: "Implementamos Session Border Controllers (SBC) de alta fidelidad con cifrado TLS/SRTP, topología oculta, listas de control de acceso (ACL), detección automática de ataques de denegación de servicio (DoS/DDoS) y mitigación de intentos de fraude de llamadas internacionales."
}, {
  question: "¿Es posible migrar gradualmente sin interrumpir las operaciones?",
  answer: "Totalmente. Diseñamos planes de transición en paralelo que aseguran cero tiempo de inactividad durante las llamadas. Su numeración principal y canales entrantes permanecen operativos en todo momento mientras se realizan las pruebas y homologaciones pertinentes."
}];
export default function ImpulseSuComunicaciN() {
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll({
    threshold: 0.15,
    once: true
  });
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  return <section ref={contentRef} className="bg-background text-foreground transition-all duration-700 opacity-100 translate-y-0 ![padding:10px_0px_10px_0px]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Testimonials Block */}
        <div className="mb-20 md:mb-28">
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-accent text-primary text-xs font-semibold uppercase tracking-wider mb-4 border border-border">
              <Quote className="size-3.5" />
              <span>Casos de Éxito</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-serif tracking-tight text-foreground">
              Confianza respaldada por líderes empresariales
            </h2>
            <p className="mt-4 text-base md:text-lg text-muted-foreground font-sans">
              Descubra cómo nuestras soluciones de telecomunicaciones y colaboración transforman la productividad diaria de organizaciones en toda la región.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {testimonials.map((item, i) => <div key={i} data-index={i} className="flex flex-col justify-between bg-card p-6 md:p-8 rounded-lg border border-border shadow-sm hover:shadow-xl hover:-translate-y-2 hover:border-primary/50 transition-all duration-300">
                <div>
                  <div className="flex items-center gap-1 mb-4 text-chart-4">
                    {Array.from({
                  length: item.rating
                }).map((_, starIndex) => <Star key={starIndex} className="size-4 fill-chart-4 text-chart-4" />)}
                  </div>
                  <p className="text-foreground/90 text-sm md:text-base leading-relaxed italic mb-6">
                    "{item.quote}"
                  </p>
                </div>

                <div className="flex items-center gap-4 pt-4 border-t border-border/60">
                  <div className="size-11 rounded-lg bg-primary/10 text-primary font-bold flex items-center justify-center text-sm tracking-wide border border-primary/20 shrink-0">
                    {item.initials}
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-foreground leading-tight">
                      {item.name}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {item.role} • <span className="text-foreground/80 font-medium">{item.company}</span>
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-20 md:mb-28 max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-accent text-primary text-xs font-semibold uppercase tracking-wider mb-4 border border-border">
              <HelpCircle className="size-3.5" />
              <span>Preguntas Frecuentes</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-serif tracking-tight text-foreground">
              Respuestas a sus dudas técnicas y operativas
            </h2>
            <p className="mt-3 text-base text-muted-foreground">
              Todo lo que necesita saber sobre la implementación, compatibilidad y seguridad de nuestras soluciones.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => {
            const isOpen = openFaqIndex === index;
            return <div key={index} data-index={index} className="rounded-lg border border-border bg-card shadow-xs transition-colors overflow-hidden">
                  <Button type="button" variant="ghost" onClick={() => toggleFaq(index)} aria-expanded={isOpen} className="w-full h-auto py-4 px-6 text-left flex items-center justify-between gap-4 font-semibold text-foreground hover:text-primary hover:bg-transparent rounded-none">
                    <span className="text-base md:text-lg font-serif whitespace-normal text-left">
                      {faq.question}
                    </span>
                    <ChevronDown className={`size-5 text-muted-foreground shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180 text-primary" : ""}`} />
                  </Button>

                  {isOpen && <div className="px-6 pb-5 pt-1 text-sm md:text-base text-muted-foreground leading-relaxed border-t border-border/40 bg-accent/30">
                      <p>{faq.answer}</p>
                    </div>}
                </div>;
          })}
          </div>
        </div>

        {/* Dual CTA Banner */}
        <div className="rounded-lg bg-gradient-to-br from-card via-accent/50 to-card border border-border p-8 md:p-12 shadow-lg relative overflow-hidden">
          <div className="absolute -right-16 -bottom-16 size-64 bg-primary/10 rounded-lg blur-3xl pointer-events-none" />
          <div className="absolute -left-16 -top-16 size-64 bg-secondary/10 rounded-lg blur-3xl pointer-events-none" />

          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-4 border border-primary/20">
              <Headphones className="size-3.5" />
              <span>Asesoría Especializada Inmediata</span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold font-serif tracking-tight text-foreground">
              Impulse la comunicación de su empresa con AVTECSA
            </h2>

            <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
              ¿Listo para modernizar su infraestructura telefónica, integrar Microsoft Teams y proteger sus canales de voz? Nuestros ingenieros certificados están disponibles para evaluar sus requerimientos.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto font-semibold px-6 shadow-md hover:scale-[1.02] transition-transform">
                <a href="tel:+50622633278" aria-label="Llamar a AVTECSA al +506 2263-3278">
                  <Phone className="size-4 mr-2" />
                  <span>Llamar Ahora</span>
                </a>
              </Button>

              <Button asChild variant="secondary" size="lg" className="w-full sm:w-auto font-semibold px-6 bg-[hsl(142,70%,45%)] text-white hover:bg-[hsl(142,70%,40%)] shadow-md hover:scale-[1.02] transition-transform">
                <Link to="https://wa.me/50687176886" newTab aria-label="Contactar por WhatsApp a AVTECSA">
                  <MessageCircle className="size-4 mr-2" />
                  <span>WhatsApp</span>
                </Link>
              </Button>
            </div>

            <div className="mt-6 flex items-center justify-center gap-6 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="size-4 text-primary" />
                Soporte certificado
              </span>
              <span>•</span>
              <span>Atención técnica directa</span>
              <span>•</span>
              <span>Sin compromiso</span>
            </div>
          </div>
        </div>

      </div>
    </section>;
}